import React, { useCallback, useState } from 'react';
import { SafeAreaView, TouchableOpacity, View } from 'react-native';
import { Button, Input, Text } from '~/components';
import useAppNavigation from '~/hooks/useAppNavigation';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmail } from '~/utils/yup';

import MarisaServiceLogoSVG from '~/assets/svgs/marisaServicesLogo.svg';
import MarisaSVG from '~/assets/svgs/marisa.svg';
import CloseSVG from '~/assets/svgs/close.svg';
import { useForm } from 'react-hook-form';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';
import ConfirmationModal from './components/ConfirmationModal';
import { sendResetPasswordEmailUseCase } from '~/useCases/HybrisAuthenticateUseCases/SendResetPasswordEmailUseCase';
import useToast from '~/hooks/useToast';

const MARISA_FAQ_URL = 'https://www.marisa.com.br/faq';

interface ForgotPasswordForm {
  email: string;
}

const schema = yup
  .object()
  .shape({
    email: isEmail(),
  })
  .required();

const ForgotPassword: React.FC = () => {
  const { goBack } = useAppNavigation();
  const { showError } = useToast();

  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: ForgotPasswordForm) => {
      try {
        await sendResetPasswordEmailUseCase(data.email);

        setConfirmationModalVisible(true);
      } catch (err: any) {
        showError({
          description: err.message,
        });
      }
    },
    [showError]
  );

  const handleFinish = useCallback(() => {
    setConfirmationModalVisible(false);
    goBack();
  }, [goBack]);

  return (
    <SafeAreaView className="bg-white h-full">
      <View className="flex-row h-full mb-24">
        <View className="flex-1 bg-primary-600 items-center justify-center hidden lg:flex">
          <MarisaServiceLogoSVG />
        </View>

        <View className="flex-1 bg-white items-center justify-start px-10 pt-24 lg:pt-[8%]">
          <MarisaSVG />

          <ConfirmationModal
            visible={confirmationModalVisible}
            onDismiss={handleFinish}
          />

          <View>
            <Text className="text-[22px] text-gray-700 mt-6 max-w-[450px] text-center mb-14">
              Redefinir senha
            </Text>

            <Input
              name="email"
              control={control}
              label="Email"
              placeholder="Insira o seu email"
              error={errors.email?.message}
              className="mb-6"
            />

            <View className="py-2 px-6 bg-health-200 max-w-[450px] rounded">
              <Text className="text-xs text-center text-gray-400">
                Se não tiver acesso a esse e-mail, entre em contato com nosso{' '}
                <Text className="font-bold text-primary-600">
                  SAC (11) 3383-7222
                </Text>{' '}
                ou acesse o nosso FAQ para demais dúvidas:{' '}
                <TouchableOpacity
                  onPress={() => MarisaAppUtils.openUrl(MARISA_FAQ_URL)}
                >
                  <Text className="font-bold text-primary-600">
                    {MARISA_FAQ_URL}
                  </Text>
                </TouchableOpacity>
              </Text>
            </View>

            <Button className="mt-6" onPress={handleSubmit(onSubmit)}>
              Próximo
            </Button>
          </View>
        </View>
        <TouchableOpacity
          className="absolute top-5 right-5 z-10"
          onPress={goBack}
        >
          <CloseSVG height={24} width={24} />
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

export default ForgotPassword;
